// Dynamics Calculators helpers: force, momentum, impulse, weight, friction,
// normal force on an incline, work, kinetic/potential energy, power, spring
// force (Hooke's Law), and torque - Newtonian dynamics (force-based), as
// opposed to lib/tools/kinematicsCalculators.ts (pure motion description,
// no mass/force involved).
//
// Scope notes (deliberate narrowing, matching this project's convention):
// - g = 9.81 m/s^2 default for Weight/Potential Energy, but exposed as an
//   editable input so users can compute weight on the Moon/Mars/etc.
// - Torque only supports the single-force, single-radius case (tau = F r sinθ),
//   not multi-force net-torque problems.
import { t } from './i18n'

const G_EARTH = 9.81 // m/s^2, standard gravity

const NO_SOLUTION_LABEL: Record<string, string> = {
    en: 'No solution', ru: 'Решения нет', lv: 'Nav risinājuma', pl: 'Brak rozwiązania',
    es: 'Sin solución', fr: 'Pas de solution', it: 'Nessuna soluzione', de: 'Keine Lösung',
}

// ============================================================
// 1. Force Calculator (F = ma)
// ============================================================
export function forceCalculator(params: { solve_for: unknown; mass_kg: unknown; acceleration_ms2: unknown; force_n: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'force')
    const m = Number(params.mass_kg) || 0
    const a = Number(params.acceleration_ms2) || 0
    const f = Number(params.force_n) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'mass') {
        if (a === 0) return { result: errText, steps: errText }
        return { result: f / a, steps: `m = F / a = ${f} / ${a} = ${(f / a).toFixed(4)} kg` }
    }
    if (solveFor === 'acceleration') {
        if (m === 0) return { result: errText, steps: errText }
        return { result: f / m, steps: `a = F / m = ${f} / ${m} = ${(f / m).toFixed(4)} m/s²` }
    }
    return { result: m * a, steps: `F = m × a = ${m} × ${a} = ${(m * a).toFixed(4)} N` }
}

// ============================================================
// 2. Momentum Calculator (p = mv)
// ============================================================
export function momentumCalculator(params: { solve_for: unknown; mass_kg: unknown; velocity_ms: unknown; momentum_kgms: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'momentum')
    const m = Number(params.mass_kg) || 0
    const v = Number(params.velocity_ms) || 0
    const p = Number(params.momentum_kgms) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'mass') {
        if (v === 0) return { result: errText, steps: errText }
        return { result: p / v, steps: `m = p / v = ${p} / ${v} = ${(p / v).toFixed(4)} kg` }
    }
    if (solveFor === 'velocity') {
        if (m === 0) return { result: errText, steps: errText }
        return { result: p / m, steps: `v = p / m = ${p} / ${m} = ${(p / m).toFixed(4)} m/s` }
    }
    return { result: m * v, steps: `p = m × v = ${m} × ${v} = ${(m * v).toFixed(4)} kg·m/s` }
}

// ============================================================
// 3. Impulse Calculator (J = m*delta_v = F * t)
// ============================================================
export function impulseCalculator(params: { mass_kg: unknown; initial_velocity_ms: unknown; final_velocity_ms: unknown; time_s: unknown; language: unknown }): {
    impulse_ns: number; avg_force_n: number | string; steps: string
} {
    const m = Math.max(Number(params.mass_kg) || 0, 0)
    const vi = Number(params.initial_velocity_ms) || 0
    const vf = Number(params.final_velocity_ms) || 0
    const tm = Math.max(Number(params.time_s) || 0, 0)
    const errText = t(NO_SOLUTION_LABEL, params.language)

    const deltaV = vf - vi
    const impulse = m * deltaV
    const avgForce = tm === 0 ? errText : impulse / tm

    const steps = [
        `Δv = vf - vi = ${vf} - ${vi} = ${deltaV.toFixed(4)} m/s`,
        `J = m × Δv = ${m} × ${deltaV.toFixed(4)} = ${impulse.toFixed(4)} N·s`,
        tm === 0 ? `F_avg = ${errText}` : `F_avg = J / t = ${impulse.toFixed(4)} / ${tm} = ${(impulse / tm).toFixed(4)} N`,
    ].join('\n')

    return { impulse_ns: impulse, avg_force_n: avgForce, steps }
}

// ============================================================
// 4. Weight Calculator (W = mg)
// ============================================================
export function weightCalculator(params: { solve_for: unknown; mass_kg: unknown; gravity_ms2: unknown; weight_n: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'weight')
    const m = Number(params.mass_kg) || 0
    const g = Number(params.gravity_ms2) || G_EARTH
    const w = Number(params.weight_n) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'mass') {
        if (g === 0) return { result: errText, steps: errText }
        return { result: w / g, steps: `m = W / g = ${w} / ${g} = ${(w / g).toFixed(4)} kg` }
    }
    return { result: m * g, steps: `W = m × g = ${m} × ${g} = ${(m * g).toFixed(4)} N` }
}

// ============================================================
// 5. Friction Force Calculator (f = mu * N)
// ============================================================
export function frictionForceCalculator(params: { solve_for: unknown; coefficient_of_friction: unknown; normal_force_n: unknown; friction_force_n: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'friction_force')
    const mu = Number(params.coefficient_of_friction) || 0
    const n = Number(params.normal_force_n) || 0
    const f = Number(params.friction_force_n) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'coefficient') {
        if (n === 0) return { result: errText, steps: errText }
        return { result: f / n, steps: `μ = f / N = ${f} / ${n} = ${(f / n).toFixed(4)}` }
    }
    if (solveFor === 'normal_force') {
        if (mu === 0) return { result: errText, steps: errText }
        return { result: f / mu, steps: `N = f / μ = ${f} / ${mu} = ${(f / mu).toFixed(4)} N` }
    }
    return { result: mu * n, steps: `f = μ × N = ${mu} × ${n} = ${(mu * n).toFixed(4)} N` }
}

// ============================================================
// 6. Normal Force / Incline Calculator
// ============================================================
export function inclineForceCalculator(params: { mass_kg: unknown; angle_deg: unknown; language: unknown }): {
    weight_n: number; normal_force_n: number; parallel_force_n: number; steps: string
} {
    const m = Math.max(Number(params.mass_kg) || 0, 0)
    const angleDeg = Math.min(Math.max(Number(params.angle_deg) || 0, 0), 89.9999)
    const rad = (angleDeg * Math.PI) / 180

    const weight = m * G_EARTH
    const normalForce = weight * Math.cos(rad)
    const parallelForce = weight * Math.sin(rad)

    const steps = [
        `W = m × g = ${m} × ${G_EARTH} = ${weight.toFixed(4)} N`,
        `N = W × cos(θ) = ${weight.toFixed(4)} × cos(${angleDeg}°) = ${normalForce.toFixed(4)} N`,
        `F_parallel = W × sin(θ) = ${weight.toFixed(4)} × sin(${angleDeg}°) = ${parallelForce.toFixed(4)} N`,
    ].join('\n')

    return { weight_n: weight, normal_force_n: normalForce, parallel_force_n: parallelForce, steps }
}

// ============================================================
// 7. Work Calculator (W = F d cos(theta))
// ============================================================
export function workCalculator(params: { force_n: unknown; distance_m: unknown; angle_deg: unknown; language: unknown }): {
    work_j: number; steps: string
} {
    const f = Number(params.force_n) || 0
    const d = Math.max(Number(params.distance_m) || 0, 0)
    const angleDeg = Number(params.angle_deg) || 0
    const rad = (angleDeg * Math.PI) / 180

    const work = f * d * Math.cos(rad)
    const steps = `W = F × d × cos(θ) = ${f} × ${d} × cos(${angleDeg}°) = ${work.toFixed(4)} J`

    return { work_j: work, steps }
}

// ============================================================
// 8. Kinetic Energy Calculator (KE = 1/2 m v^2)
// ============================================================
export function kineticEnergyCalculator(params: { solve_for: unknown; mass_kg: unknown; velocity_ms: unknown; kinetic_energy_j: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'kinetic_energy')
    const m = Number(params.mass_kg) || 0
    const v = Number(params.velocity_ms) || 0
    const ke = Math.max(Number(params.kinetic_energy_j) || 0, 0)
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'mass') {
        if (v === 0) return { result: errText, steps: errText }
        const result = (2 * ke) / (v * v)
        return { result, steps: `m = 2×KE / v² = 2×${ke} / ${v}² = ${result.toFixed(4)} kg` }
    }
    if (solveFor === 'velocity') {
        if (m <= 0) return { result: errText, steps: errText }
        const result = Math.sqrt((2 * ke) / m)
        return { result, steps: `v = √(2×KE / m) = √(2×${ke} / ${m}) = ${result.toFixed(4)} m/s` }
    }
    const result = 0.5 * m * v * v
    return { result, steps: `KE = ½ × m × v² = 0.5 × ${m} × ${v}² = ${result.toFixed(4)} J` }
}

// ============================================================
// 9. Potential Energy Calculator (PE = m g h)
// ============================================================
export function potentialEnergyCalculator(params: { solve_for: unknown; mass_kg: unknown; height_m: unknown; gravity_ms2: unknown; potential_energy_j: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'potential_energy')
    const m = Number(params.mass_kg) || 0
    const h = Number(params.height_m) || 0
    const g = Number(params.gravity_ms2) || G_EARTH
    const pe = Number(params.potential_energy_j) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'mass') {
        if (g === 0 || h === 0) return { result: errText, steps: errText }
        const result = pe / (g * h)
        return { result, steps: `m = PE / (g×h) = ${pe} / (${g}×${h}) = ${result.toFixed(4)} kg` }
    }
    if (solveFor === 'height') {
        if (m === 0 || g === 0) return { result: errText, steps: errText }
        const result = pe / (m * g)
        return { result, steps: `h = PE / (m×g) = ${pe} / (${m}×${g}) = ${result.toFixed(4)} m` }
    }
    const result = m * g * h
    return { result, steps: `PE = m × g × h = ${m} × ${g} × ${h} = ${result.toFixed(4)} J` }
}

// ============================================================
// 10. Power Calculator (P = W / t)
// ============================================================
export function powerCalculator(params: { solve_for: unknown; work_j: unknown; time_s: unknown; power_w: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'power')
    const w = Number(params.work_j) || 0
    const tm = Number(params.time_s) || 0
    const p = Number(params.power_w) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'work') {
        return { result: p * tm, steps: `W = P × t = ${p} × ${tm} = ${(p * tm).toFixed(4)} J` }
    }
    if (solveFor === 'time') {
        if (p === 0) return { result: errText, steps: errText }
        return { result: w / p, steps: `t = W / P = ${w} / ${p} = ${(w / p).toFixed(4)} s` }
    }
    if (tm === 0) return { result: errText, steps: errText }
    return { result: w / tm, steps: `P = W / t = ${w} / ${tm} = ${(w / tm).toFixed(4)} W` }
}

// ============================================================
// 11. Spring Force Calculator (Hooke's Law: F = kx), also returns elastic PE
// ============================================================
export function springForceCalculator(params: { solve_for: unknown; spring_constant_nm: unknown; displacement_m: unknown; force_n: unknown; language: unknown }): {
    result: number | string; elastic_pe_j: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'force')
    const k = Number(params.spring_constant_nm) || 0
    const x = Number(params.displacement_m) || 0
    const f = Number(params.force_n) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'spring_constant') {
        if (x === 0) return { result: errText, elastic_pe_j: errText, steps: errText }
        const result = f / x
        const pe = 0.5 * result * x * x
        return { result, elastic_pe_j: pe, steps: `k = F / x = ${f} / ${x} = ${result.toFixed(4)} N/m\nPE = ½kx² = 0.5 × ${result.toFixed(4)} × ${x}² = ${pe.toFixed(4)} J` }
    }
    if (solveFor === 'displacement') {
        if (k === 0) return { result: errText, elastic_pe_j: errText, steps: errText }
        const result = f / k
        const pe = 0.5 * k * result * result
        return { result, elastic_pe_j: pe, steps: `x = F / k = ${f} / ${k} = ${result.toFixed(4)} m\nPE = ½kx² = 0.5 × ${k} × ${result.toFixed(4)}² = ${pe.toFixed(4)} J` }
    }
    const result = k * x
    const pe = 0.5 * k * x * x
    return { result, elastic_pe_j: pe, steps: `F = k × x = ${k} × ${x} = ${result.toFixed(4)} N\nPE = ½kx² = 0.5 × ${k} × ${x}² = ${pe.toFixed(4)} J` }
}

// ============================================================
// 12. Torque Calculator (tau = F r sin(theta))
// ============================================================
export function torqueCalculator(params: { solve_for: unknown; force_n: unknown; radius_m: unknown; angle_deg: unknown; torque_nm: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'torque')
    const f = Number(params.force_n) || 0
    const r = Number(params.radius_m) || 0
    const angleDeg = Number(params.angle_deg) ?? 90
    const tau = Number(params.torque_nm) || 0
    const rad = (angleDeg * Math.PI) / 180
    const sinTheta = Math.sin(rad)
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'force') {
        if (r === 0 || sinTheta === 0) return { result: errText, steps: errText }
        const result = tau / (r * sinTheta)
        return { result, steps: `F = τ / (r × sin(θ)) = ${tau} / (${r} × sin(${angleDeg}°)) = ${result.toFixed(4)} N` }
    }
    if (solveFor === 'radius') {
        if (f === 0 || sinTheta === 0) return { result: errText, steps: errText }
        const result = tau / (f * sinTheta)
        return { result, steps: `r = τ / (F × sin(θ)) = ${tau} / (${f} × sin(${angleDeg}°)) = ${result.toFixed(4)} m` }
    }
    const result = f * r * sinTheta
    return { result, steps: `τ = F × r × sin(θ) = ${f} × ${r} × sin(${angleDeg}°) = ${result.toFixed(4)} N·m` }
}
